import React, {
	useMemo,
	useState,
	useCallback,
	useRef,
	useEffect,
} from 'react';

import './NocChargerPerfomanceSidePanel.css';

import {
	HorizontalNavbar,
	useAppSelector,
} from '../../globalUtils/globalExports';

import LinearStatusBar from './LinearStatusBar';
import NocChargerReport from './NocChargerReport';

import { getNOCDashboardReducer } from '../../rmsReduxStore/reduxExports';

type PerformanceRankingNav = 'uptime' | 'utilisation' | 'chargingEfficiency';

type ChargerNav = 'onlinechargers' | 'offlinechargers' | 'chargingsessions';

interface NocChargerPerfomanceSidePanelProps {
	handlePerformanceRankingNavChange: (value: PerformanceRankingNav) => void;
	showMapInFullView?: boolean;
	
}

const NAV_ORDER: ChargerNav[] = [
	'onlinechargers',
	'offlinechargers',
	'chargingsessions',
];

const NocChargerPerfomanceSidePanel: React.FC<
	NocChargerPerfomanceSidePanelProps
> = ({ handlePerformanceRankingNavChange }) => {
	const { nocDashboardChargerPerfomanceData } = useAppSelector(
		getNOCDashboardReducer,
	);

	const [selectedNav, setSelectedNav] = useState<ChargerNav>('onlinechargers');

	const userInteractedRef = useRef(false);

	const getPercentage = (value: number, total: number): number => {
		if (value === 0 && total === 0) return 50;
		return total > 0 ? Math.round((value / total) * 100) : 0;
	};

	const navCountMap = useMemo(
		() => ({
			onlinechargers: nocDashboardChargerPerfomanceData?.onlineChargers ?? 0,
			offlinechargers: nocDashboardChargerPerfomanceData?.offlineChargers ?? 0,
			chargingsessions:
				nocDashboardChargerPerfomanceData?.totalChargingSessions ?? 0,
		}),
		[nocDashboardChargerPerfomanceData],
	);

	const headerData = useMemo(
		() => [
			{
				id: 'onlinechargers',
				text: `Online (${navCountMap.onlinechargers})`,
				isAccessible: true,
			},
			{
				id: 'offlinechargers',
				text: `Offline (${navCountMap.offlinechargers})`,
				isAccessible: true,
			},
			{
				id: 'chargingsessions',
				text: `Sessions (${navCountMap.chargingsessions})`,
				isAccessible: true,
			},
		],
		[navCountMap],
	);

	const handleNavBtnClick = useCallback((val: ChargerNav) => {
		userInteractedRef.current = true;
		setSelectedNav(val);
	}, []);

	const renderOnlineContent = () => {
		const health = nocDashboardChargerPerfomanceData?.healthStatus;
		const charging = nocDashboardChargerPerfomanceData?.chargingStatus;
		const guns = nocDashboardChargerPerfomanceData?.chargerGunStatus?.[0];

		return (
			<>
				<div className='noc-charger-status-bar-container'>
					<LinearStatusBar
						title='Health'
						leftLabel='Healthy'
						rightLabel='Faulty'
						type='Chargers'
						value={getPercentage(
							health?.healthyChargers ?? 0,
							(health?.healthyChargers ?? 0) + (health?.unhealthyChargers ?? 0),
						)}
						rightLabelValue={health?.healthyChargers ?? 0}
						totalValue={
							(health?.healthyChargers ?? 0) + (health?.unhealthyChargers ?? 0)
						}
						totalChargers={
							nocDashboardChargerPerfomanceData?.onlineChargers ?? 0
						}
						tooltipLabel= 'Online Chargers'
					/>

					<LinearStatusBar
						title='Charging Status'
						leftLabel='Charging'
						rightLabel='Idle'
						type='Chargers'
						value={getPercentage(
							charging?.busyChargers ?? 0,
							(charging?.busyChargers ?? 0) + (charging?.idleChargers ?? 0),
						)}
						rightLabelValue={charging?.busyChargers ?? 0}
						totalValue={
							(charging?.busyChargers ?? 0) + (charging?.idleChargers ?? 0)
						}
						totalChargers={
							nocDashboardChargerPerfomanceData?.onlineChargers ?? 0
						}
						tooltipLabel= 'Online Chargers'
					/>
				</div>

				<div className='noc-charger-status-bar-heading'>Connectors</div>

				<div className='noc-charger-status-bar-container'>
					<LinearStatusBar
						title='Operational Status'
						leftLabel='Operational'
						rightLabel='Non-Operational'
						type='Connectors'
						value={getPercentage(
							guns?.operationalGuns ?? 0,
							(guns?.operationalGuns ?? 0) + (guns?.nonOperationalGuns ?? 0),
						)}
						rightLabelValue={guns?.operationalGuns ?? 0}
						totalValue={
							(guns?.operationalGuns ?? 0) + (guns?.nonOperationalGuns ?? 0)
						}
						totalChargers={
							(guns?.operationalGuns ?? 0) + (guns?.nonOperationalGuns ?? 0)
						}
						tooltipLabel= {`Connectors (${nocDashboardChargerPerfomanceData?.onlineChargers} Online Chargers)`}
					/>

					<LinearStatusBar
						title='Availability'
						leftLabel='Occupied'
						rightLabel='Available'
						type='Operational Connectors'
						value={getPercentage(
							guns?.occupiedGuns ?? 0,
							(guns?.occupiedGuns ?? 0) + (guns?.availableGuns ?? 0),
						)}
						rightLabelValue={guns?.occupiedGuns ?? 0}
						totalValue={(guns?.occupiedGuns ?? 0) + (guns?.availableGuns ?? 0)}
						totalChargers={guns?.operationalGuns ?? 0}
						tooltipLabel= {`Operational Connectors (${nocDashboardChargerPerfomanceData?.onlineChargers} Online Chargers)`}
					/>
				</div>
			</>
		);
	};

	const renderOfflineContent = () => (
		<div className='noc-charger-perfomance-offline'>
			<div className='noc-charger-perfomance-metric-container'>
				<div>RMS Disabled</div>
				<strong>
					{nocDashboardChargerPerfomanceData?.rmsDisabledChargers ?? '--'}
				</strong>
			</div>

			<div className='noc-charger-perfomance-metric-container'>
				<div>CMS Disabled</div>
				<strong>
					{nocDashboardChargerPerfomanceData?.cmsDisabledChargers ?? '--'}
				</strong>
			</div>
		</div>
	);

	const renderChargingSessionContent = () => (
		<div className='noc-charger-status-bar-container'>
			<LinearStatusBar
				title='Sessions'
				leftLabel='Success'
				rightLabel='Failed'
				type='Sessions'
				value={getPercentage(
					nocDashboardChargerPerfomanceData?.successFullSessions ?? 0,
					(nocDashboardChargerPerfomanceData?.successFullSessions ?? 0) +
						(nocDashboardChargerPerfomanceData?.failedSessions ?? 0),
				)}
				rightLabelValue={
					nocDashboardChargerPerfomanceData?.successFullSessions ?? 0
				}
				totalValue={
					(nocDashboardChargerPerfomanceData?.successFullSessions ?? 0) +
					(nocDashboardChargerPerfomanceData?.failedSessions ?? 0)
				}
				tooltipLabel='Sessions'
			/>

			<div className='noc-charger-perfomance-offline'>
				<div className='noc-charger-perfomance-metric-container'>
					<div>Energy Dispensed</div>
					<strong>
						{nocDashboardChargerPerfomanceData?.energyDispensed?.toFixed(2) ??
							0}{' '}
						kWh
					</strong>
				</div>

				<div className='noc-charger-perfomance-metric-container'>
					<div>Average Session Time</div>
					<strong>
						{nocDashboardChargerPerfomanceData?.averageSessionTime?.toFixed(
							2,
						) ?? '--'}
					</strong>
				</div>
			</div>
		</div>
	);

	const renderContent = () => {
		switch (selectedNav) {
			case 'onlinechargers':
				return renderOnlineContent();
			case 'offlinechargers':
				return renderOfflineContent();
			case 'chargingsessions':
				return renderChargingSessionContent();
			default:
				return null;
		}
	};

	useEffect(() => {
		const intervalId = setInterval(() => {
			if (userInteractedRef.current) return;

			setSelectedNav((prev) => {
				const currentIndex = NAV_ORDER.indexOf(prev);
				return NAV_ORDER[(currentIndex + 1) % NAV_ORDER.length];
			});
		}, 5_000);

		return () => clearInterval(intervalId);
	}, []);

	return (
		<div className='noc-charger-perfomance-panel'>
			<div className='noc-charger-perfomance-card-container'>
				<div className='noc-charger-perfomance-header'>
					<div>Chargers</div>
					<div>{nocDashboardChargerPerfomanceData?.chargers}</div>
				</div>

				<div className='noc-charger-panel-border' />

				<div className='noc-charger-nav-bar'>
					<HorizontalNavbar
						selectedNav={selectedNav}
						navList={headerData}
						onNavClick={(event, val) => {
							event?.stopPropagation();
							handleNavBtnClick(val as ChargerNav);
						}}
					/>
				</div>

				{renderContent()}
			</div>

			<div className='noc-charger-report-card-container'>
				<NocChargerReport
					handlePerformanceRankingNavChange={handlePerformanceRankingNavChange}
				/>
			</div>
		</div>
	);
};

export default NocChargerPerfomanceSidePanel;
